import { useState, useEffect } from 'react';
import { Outlet, useNavigate, useSearchParams, useLocation } from 'react-router-dom';
import { Settings, Heart, LogOut, ChevronRight, MoreVertical, User, CreditCard, Rocket, DollarSign, Home, Church } from 'lucide-react';
import { useAuth } from '@/components/AuthProvider';
import { upgradeToCreator } from '@/lib/firebase/auth';
import { toast } from 'sonner';
import { collection, query, where, getDocs, doc, DocumentData, setDoc, serverTimestamp, limit } from 'firebase/firestore';
import { db } from '@/lib/firebase/config';
import { resolveUserIdFromIdentifier } from '@/lib/firebase/user';
import { getPublicProfile } from '@/lib/firebase/user/publicProfile';
import {
    DropdownMenu,
    DropdownMenuContent,
    DropdownMenuItem,
    DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import BecomeCreatorModal from '@/components/ui/BecomeCreatorModal';
import { cn } from '@/lib/utils/styles';
import { useTranslation } from 'react-i18next';

interface Creator {
    id: string;
    name: string;
    path?: string; // Friendly URL path like "john-doe"
}

interface UserData {
    firstName: string;
    lastName: string;
    displayName?: string;
    path?: string;
}

function MemberNavigation() {
    const { user, userRoles, signOut } = useAuth();
    const { t } = useTranslation();
    const navigate = useNavigate();
    const location = useLocation();
    const [searchParams] = useSearchParams();
    const [supportedCreators, setSupportedCreators] = useState<Creator[]>([]);
    const [isLoadingCreators, setIsLoadingCreators] = useState(true);
    const [showBecomeCreatorModal, setShowBecomeCreatorModal] = useState(false);
    const [hasDonations, setHasDonations] = useState(true);

    useEffect(() => {
        async function loadSupportedCreators() {
            if (!user?.uid) return;

            try {
                // Load all active subscriptions
                const q = query(
                    collection(db, 'subscriptions'),
                    where('userId', '==', user.uid),
                    where('status', '==', 'active')
                );

                const snapshot = await getDocs(q);
                const creatorsMap = new Map<string, Creator>();

                const creatorPromises = snapshot.docs.map(async (docSnapshot) => {
                    const subData = docSnapshot.data();
                    if (!subData.creatorId) return null;

                    // De-dupe by creatorId
                    if (creatorsMap.has(subData.creatorId)) return null;

                    const creatorData = await getPublicProfile(subData.creatorId) as UserData | null;
                    if (creatorData) {
                        const creator: Creator = {
                            id: subData.creatorId,
                            name: creatorData.displayName || `${creatorData.firstName} ${creatorData.lastName}`,
                            path: creatorData.path // Store the friendly URL path
                        };
                        creatorsMap.set(subData.creatorId, creator);
                        return creator;
                    }
                    return null;
                });

                const creators = (await Promise.all(creatorPromises)).filter((c): c is Creator => c !== null);
                setSupportedCreators(Array.from(creatorsMap.values()));
            } catch (error) {
                console.error('Error loading supported creators:', error);
            } finally {
                setIsLoadingCreators(false);
            }
        }

        loadSupportedCreators();
    }, [user?.uid, searchParams.get('success'), location.search]);

    // Always show Donations link per product requirement

    // Optimistic follow insertion after successful checkout redirect
    useEffect(() => {
        async function ensureSubscriptionFromRedirect() {
            if (!user?.uid) return;
            const params = new URLSearchParams(location.search);
            if (params.get('supported') !== '1') return;
            const match = location.pathname.match(/\/dashboard\/mission\/(.+)$/);
            const creatorIdentifier = match?.[1];
            if (!creatorIdentifier) return;
            try {
                // Resolve the identifier (could be path or ID) to actual user ID
                const creatorId = await resolveUserIdFromIdentifier(creatorIdentifier);
                if (!creatorId) return;

                const subId = `${user.uid}_${creatorId}_donor_paid`;
                await setDoc(doc(db, 'subscriptions', subId), {
                    userId: user.uid,
                    creatorId,
                    membershipId: null,
                    status: 'active',
                    stripeSubscriptionId: null,
                    amount: 0,
                    subscriptionType: 'paid',
                    currentPeriodStart: serverTimestamp(),
                    cancelAtPeriodEnd: false,
                    createdAt: serverTimestamp(),
                    updatedAt: serverTimestamp(),
                }, { merge: true });
                // Refresh list but avoid duplicates
                setSupportedCreators((prev) => prev.some(c => c.id === creatorId) ? prev : [...prev, { id: creatorId, name: '', path: creatorIdentifier !== creatorId ? creatorIdentifier : undefined }]);
            } catch (e) {
                console.warn('[MemberNavigation] failed to upsert subscription after redirect', e);
            }
        }
        ensureSubscriptionFromRedirect();
    }, [user?.uid, location.pathname, location.search]);

    return (
        <nav className="h-full flex flex-col md:fixed w-full md:w-64 max-h-[45vh] md:max-h-none overflow-y-auto md:overflow-visible">
            <div className="flex-1 px-2 space-y-1 pt-4 overflow-y-auto">
                {/* Logo */}
                <div className="mb-6 flex justify-start px-2 py-2">
                    <button
                        onClick={() => navigate('/dashboard')}
                        className="focus:outline-none hover:opacity-80 transition-opacity"
                    >
                        <img src="/devotionfund-logo.png" alt="Devotion Fund Logo" className="h-8" />
                    </button>
                </div>

                {/* I'm Supporting Section */}
                <div className="mb-12">
                    <h3 className="px-3 text-xs font-semibold text-gray-500 uppercase tracking-wider">
                        {t('dashboard.navigation.supporting')}
                    </h3>
                    <div className="mt-2 space-y-1">
                        {/* Dashboard Home */}
                        <button
                            onClick={() => navigate('/dashboard')}
                            className={cn(
                                "flex items-center w-full px-2 py-2 text-sm font-medium rounded-md group",
                                location.pathname === '/dashboard' || location.pathname === '/dashboard/'
                                    ? "bg-gray-100 text-gray-900"
                                    : "text-gray-600 hover:bg-gray-50 hover:text-gray-900"
                            )}
                        >
                            <Home className="mr-3 h-5 w-5" />
                            Dashboard
                        </button>

                        {isLoadingCreators ? (
                            <div className="px-3 py-2">
                                <div className="animate-pulse h-4 bg-gray-200 rounded w-3/4"></div>
                            </div>
                        ) : supportedCreators.length > 0 ? (
                            supportedCreators.map((creator) => {
                                // Use path if available, fall back to ID for older accounts
                                const creatorUrl = creator.path || creator.id;
                                return (
                                    <button
                                        key={creator.id}
                                        onClick={() => navigate(`/dashboard/mission/${creatorUrl}`)}
                                        className={cn(
                                            "flex items-center w-full px-2 py-2 text-sm font-medium rounded-md group",
                                            location.pathname === `/dashboard/mission/${creatorUrl}`
                                                ? "bg-gray-100 text-gray-900"
                                                : "text-gray-600 hover:bg-gray-50 hover:text-gray-900"
                                        )}
                                    >
                                        <Heart className="mr-3 h-5 w-5" />
                                        {creator.name}
                                    </button>
                                );
                            })
                        ) : (
                            <p className="px-3 py-2 text-sm text-gray-500">{t('dashboard.navigation.noCreatorsYet')}</p>
                        )}

                        {/* Subscriptions page removed; donors manage via Donations -> Manage (Stripe portal) */}

                        {hasDonations && (
                            <button
                                onClick={() => navigate('/dashboard/donations')}
                                className={cn(
                                    "flex items-center w-full px-2 py-2 text-sm font-medium rounded-md group",
                                    location.pathname === '/dashboard/donations'
                                        ? "bg-gray-100 text-gray-900"
                                        : "text-gray-600 hover:bg-gray-50 hover:text-gray-900"
                                )}
                            >
                                <DollarSign className="mr-3 h-5 w-5" />
                                Donations
                            </button>
                        )}
                    </div>
                </div>

                {/* Spacer */}
                <div className="h-8"></div>

                {/* My Profile Section */}
                <div className="mb-8">
                    <h3 className="px-3 text-xs font-semibold text-gray-500 uppercase tracking-wider">
                        {t('dashboard.navigation.myProfile')}
                    </h3>
                    <div className="mt-2 space-y-1">
                        <button
                            onClick={() => navigate('/dashboard/settings')}
                            className={cn(
                                "flex items-center w-full px-2 py-2 text-sm font-medium rounded-md group",
                                location.pathname === '/dashboard/settings'
                                    ? "bg-gray-100 text-gray-900"
                                    : "text-gray-600 hover:bg-gray-50 hover:text-gray-900"
                            )}
                        >
                            <Settings className="mr-3 h-5 w-5" />
                            {t('dashboard.navigation.settings')}
                        </button>

                        {/* /church works for everyone: manage your pages, or find and claim / add one. */}
                        <button
                            onClick={() => navigate('/church')}
                            className="flex items-center w-full px-2 py-2 text-sm font-medium text-gray-600 rounded-md hover:bg-gray-50 hover:text-gray-900 group"
                        >
                            <Church className="mr-3 h-5 w-5" />
                            {userRoles?.church ? 'My church' : 'Add your church'}
                        </button>

                        {!userRoles?.creator ? (
                            <button
                                onClick={() => setShowBecomeCreatorModal(true)}
                                className="flex items-center w-full px-2 py-2 text-sm font-medium text-gray-600 rounded-md hover:bg-gray-50 hover:text-gray-900 group"
                            >
                                <Rocket className="mr-3 h-5 w-5" />
                                {t('dashboard.navigation.becomeCreator')}
                            </button>
                        ) : (
                            <button
                                onClick={() => navigate('/creator')}
                                className={cn(
                                    "flex items-center w-full px-2 py-2 text-sm font-medium rounded-md group",
                                    location.pathname.startsWith('/creator')
                                        ? "bg-gray-100 text-gray-900"
                                        : "text-gray-600 hover:bg-gray-50 hover:text-gray-900"
                                )}
                            >
                                <User className="mr-3 h-5 w-5" />
                                Creator Dashboard
                            </button>
                        )}

                        <button
                            onClick={async () => {
                                try {
                                    await signOut();
                                } catch (e) {
                                    toast.error(t('toasts.signOutFailed'));
                                }
                            }}
                            className="flex items-center w-full px-2 py-2 text-sm font-medium text-gray-600 rounded-md hover:bg-gray-50 hover:text-gray-900 group"
                        >
                            <LogOut className="mr-3 h-5 w-5" />
                            {t('actions.logout')}
                        </button>
                    </div>
                </div>
            </div>

            {/* Become Creator Modal */}
            <BecomeCreatorModal isOpen={showBecomeCreatorModal} onClose={() => setShowBecomeCreatorModal(false)} />
        </nav>
    );
}

export default function MemberDashboard() {
    const location = useLocation();
    const isMissionRoute = location.pathname.startsWith('/dashboard/mission/');

    // Same shell, and the same bug, as DashboardLayout: a 256px fixed sidebar
    // plus an unconditional ml-64 put most of this view off the right edge of
    // a phone. Found by reading the LIVE bundles after the DashboardLayout fix
    // shipped -- the guard only scanned that one file, so it stayed green
    // while its own sibling was broken. It now scans the whole directory.
    return (
        <div className="flex flex-col md:flex-row min-h-screen bg-gray-50">
            <div className="w-full md:fixed md:w-64 md:h-full border-b md:border-b-0 md:border-r bg-white">
                <MemberNavigation />
            </div>
            <div className={cn("flex-1 min-w-0 md:ml-64", isMissionRoute ? "bg-white" : undefined)}>
                {isMissionRoute ? (
                    // Full-bleed layout for mission view (no padding, no max width)
                    <Outlet />
                ) : (
                    <main className="p-6">
                        <div className="max-w-5xl mx-auto">
                            <Outlet />
                        </div>
                    </main>
                )}
            </div>
        </div>
    );
} 